// src/components/issues/SectionDocumentation.tsx — SCE Issues
// Carte « Documentation » de la fiche problème (Lot 13).
//
// Les documents qui servent à analyser le défaut, renseignés à la déclaration
// (facultatif) ou APRÈS, par n'importe quel interne (15/10) :
//  - ELEC / AUTOM : le schéma électrique (EDE) — numéro, révision, page(s) ;
//  - MECA : le N° de plan, déplacé ici depuis la carte Suivi.
// Rien pour les autres métiers : la carte n'apparaît pas.
//
// Révision (décision du 25/09) : le problème garde la sienne, et la fiche
// précise la plus récente connue pour l'équipement ; un problème sans EDE
// montre celle de l'équipement, « reprise ». Règles :
// utils/documentationEDE.ts.
//
// Chaque enregistrement : une requête, sans événement dans l'activité depuis le
// 23/10 — c'est une correction : son historique se lit sur la ligne (icône,
// versions SharePoint, utils/historiqueChamps).

import { useMemo, useState, type CSSProperties } from 'react'
import { PenLine } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { useSnackbar } from '@/components/ui/Snackbar'
import { useConfirmerEcriture } from '@/features/vue-role/contexte'
import { useReferencesEDE } from '@/hooks/useReferencesEDE'
import { renseignerDocumentationEDE, renseignerNumeroPlan } from '@/api/issues.api'
import {
  concerneEDE,
  edeAffichee,
  libelleEDE,
  type DocumentationEDE,
} from '@/utils/documentationEDE'
import { normaliserNumeroPlan, type Issue } from '@/types/issue.types'
import { LigneSuivi, STYLE_CARTE, STYLE_TITRE_CARTE } from './CarteFiche'
import { BlocHistorique, BoutonHistorique } from './HistoriqueChamp'
import { LIBELLES_HISTORIQUE, type ChampHistorise, type HistoriqueProbleme } from '@/utils/historiqueChamps'

interface Props {
  issue: Issue
  itemId: string | null
  /**
   * Tous les internes (15/10) — EDE comme N° de plan : le numéro se lit sur le
   * document que l'opérateur a sous les yeux. Problème non annulé, jamais un
   * fournisseur.
   */
  peutEditer: boolean
  /** Après une écriture réussie : la fiche le dit et se relit (pas d'événement). */
  onModifie: (message: string) => Promise<void>
  /** Versions de la fiche (null : pas encore lues) : l'icône d'historique de chaque ligne. */
  historique: HistoriqueProbleme | null
  /**
   * Tous les problèmes, lus UNE fois par la fiche (qui en a aussi besoin pour
   * les machines du même défaut, Lot 14) : c'est là qu'on apprend la révision
   * la plus récente de l'EDE.
   */
  tous: readonly Issue[]
}

export function SectionDocumentation(props: Props): JSX.Element | null {
  if (props.issue.metier === 'MECA') {
    return (
      <section style={STYLE_CARTE}>
        <h3 style={STYLE_TITRE_CARTE}>Documentation</h3>
        <LigneNumeroPlan {...props} />
      </section>
    )
  }
  if (concerneEDE(props.issue.metier)) {
    return (
      <section style={STYLE_CARTE}>
        <h3 style={STYLE_TITRE_CARTE}>Documentation</h3>
        <LignesEDE {...props} />
      </section>
    )
  }
  return null
}

// ─── Historique d'une ligne (23/10) ───

function useHistoriqueLigne(historique: HistoriqueProbleme | null, champ: ChampHistorise): { icone: JSX.Element | null; bloc: JSX.Element | null } {
  const [ouvert, setOuvert] = useState(false)
  const textes = historique?.[champ] ?? []
  if (textes.length < 2) return { icone: null, bloc: null }
  return {
    icone: <BoutonHistorique textes={textes} libelle={LIBELLES_HISTORIQUE[champ]} ouvert={ouvert} onBasculer={() => setOuvert((o) => !o)} />,
    bloc: ouvert ? <BlocHistorique textes={textes} /> : null,
  }
}

// ─── Bouton crayon, commun aux deux lignes ───

function BoutonCrayon({ libelle, onClick }: { libelle: string; onClick: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={libelle}
      title={libelle}
      className="flex items-center justify-center flex-shrink-0"
      style={{
        width: 44,
        height: 44,
        margin: '-12px -12px -12px 0',
        border: 'none',
        background: 'transparent',
        color: 'var(--color-black)',
      }}
    >
      <PenLine size={16} />
    </button>
  )
}

const STYLE_CHAMP: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '8px 10px',
  borderRadius: 8,
  border: '1px solid var(--color-black)',
  fontSize: 15,
  fontFamily: 'inherit',
  minWidth: 0,
}

/** Annuler / Enregistrer : se partagent la largeur, passent l'un sous l'autre s'il le faut. */
function BoutonsFormulaire({ enCours, onAnnuler, onEnregistrer }: { enCours: boolean; onAnnuler: () => void; onEnregistrer: () => void }): JSX.Element {
  return (
    <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
      <Button variant="ghost" size="sm" onClick={onAnnuler} disabled={enCours} style={{ flex: '1 1 auto' }}>
        Annuler
      </Button>
      <Button variant="primary" size="sm" onClick={onEnregistrer} disabled={enCours} style={{ flex: '1 1 auto' }}>
        Enregistrer
      </Button>
    </div>
  )
}

// ─── MECA : N° de plan (déplacé de la carte Suivi, inchangé) ───

function LigneNumeroPlan({ issue, itemId, peutEditer, onModifie, historique }: Props): JSX.Element {
  const { icone, bloc } = useHistoriqueLigne(historique, 'numeroPlan')
  const confirmerEcriture = useConfirmerEcriture()
  const { show } = useSnackbar()
  const [saisie, setSaisie] = useState<string | null>(null)
  const [enCours, setEnCours] = useState(false)

  async function enregistrer(): Promise<void> {
    if (!itemId || saisie === null || enCours) return
    const avant = issue.numeroPlan ?? ''
    const nouveau = normaliserNumeroPlan(saisie)
    if (nouveau === avant) {
      setSaisie(null)
      return
    }
    if (!confirmerEcriture('Enregistrer le N° de plan')) return
    setEnCours(true)
    try {
      await renseignerNumeroPlan(itemId, nouveau, issue)
      await onModifie(nouveau === '' ? 'N° de plan retiré' : 'N° de plan enregistré')
      setSaisie(null)
    } catch (err) {
      show(err instanceof Error ? err.message : "Erreur d'enregistrement", 'error')
    } finally {
      setEnCours(false)
    }
  }

  // En édition, le libellé passe AU-DESSUS du champ : dans la colonne de
  // droite, la grille « libellé | valeur » ne laissait qu'une centaine de
  // pixels au champ et aux boutons, qui sortaient de la carte (26/09).
  if (saisie !== null) {
    return (
      <div className="flex flex-col gap-2" style={{ padding: '10px 0 12px', borderTop: '1px solid var(--color-gray-10)', fontSize: 14 }}>
        <label htmlFor="numero-plan" style={{ color: 'var(--color-gray-70)' }}>
          N° de plan — un par ligne
        </label>
        <textarea
          id="numero-plan"
          value={saisie}
          onChange={(e) => setSaisie(e.target.value)}
          rows={3}
          placeholder="Un plan par ligne"
          autoFocus
          disabled={enCours}
          style={{ ...STYLE_CHAMP, resize: 'vertical' }}
        />
        <BoutonsFormulaire enCours={enCours} onAnnuler={() => setSaisie(null)} onEnregistrer={() => void enregistrer()} />
      </div>
    )
  }

  return (
    <LigneSuivi
      label="N° de plan"
      discrete={!issue.numeroPlan}
      action={peutEditer ? (
        <BoutonCrayon
          libelle={issue.numeroPlan ? 'Modifier le N° de plan' : 'Renseigner le N° de plan'}
          onClick={() => setSaisie(issue.numeroPlan ?? '')}
        />
      ) : undefined}
    >
      {issue.numeroPlan ?? 'Non renseigné'}
      {icone}
      {bloc}
    </LigneSuivi>
  )
}

// ─── ELEC / AUTOM : schéma électrique ───

function LignesEDE({ issue, itemId, peutEditer, onModifie, tous, historique }: Props): JSX.Element {
  const { icone, bloc } = useHistoriqueLigne(historique, 'ede')
  const confirmerEcriture = useConfirmerEcriture()
  const { show } = useSnackbar()
  // Les autres problèmes du même EDE — même équipement, ou même NVS (26/09) :
  // c'est là qu'on apprend la révision la plus récente.
  const avecCeluiCi = useMemo(() => [issue, ...tous.filter((i) => i.id !== issue.id)], [issue, tous])
  const { refs, cle } = useReferencesEDE(avecCeluiCi)
  const ede = edeAffichee(issue, refs, cle)

  const [saisie, setSaisie] = useState<{ numero: string; rev: string; pages: string } | null>(null)
  const [enCours, setEnCours] = useState(false)

  function ouvrir(): void {
    // Sans EDE à lui, le formulaire part de celle de l'équipement : il ne
    // reste souvent que la page à indiquer.
    setSaisie({
      numero: issue.numeroEDE ?? ede?.numero ?? '',
      rev: issue.revEDE ?? ede?.rev ?? '',
      pages: issue.pageEDE ?? '',
    })
  }

  async function enregistrer(): Promise<void> {
    if (!itemId || !saisie || enCours) return
    const avant: DocumentationEDE = {
      numeroEDE: issue.numeroEDE ?? '',
      revEDE: issue.revEDE ?? '',
      pageEDE: issue.pageEDE ?? '',
    }
    if (!confirmerEcriture('Enregistrer le schéma électrique')) return
    setEnCours(true)
    try {
      const apres = await renseignerDocumentationEDE(itemId, saisie, issue)
      const inchange =
        apres.numeroEDE === avant.numeroEDE && apres.revEDE === avant.revEDE && apres.pageEDE === avant.pageEDE
      if (!inchange) await onModifie('Schéma électrique enregistré')
      setSaisie(null)
    } catch (err) {
      // Saisie hors format : le message dit quoi corriger, rien n'est parti.
      show(err instanceof Error ? err.message : "Erreur d'enregistrement", 'error')
    } finally {
      setEnCours(false)
    }
  }

  if (saisie) {
    const champ = (
      id: string,
      libelle: string,
      cle: 'numero' | 'rev' | 'pages',
      placeholder: string,
      largeur?: number,
    ): JSX.Element => (
      <div className="flex flex-col" style={{ gap: 4, minWidth: 0, ...(largeur ? { width: largeur, flexShrink: 0 } : { flex: '1 1 0' }) }}>
        <label htmlFor={id} style={{ fontSize: 12, color: 'var(--color-gray-70)' }}>{libelle}</label>
        <input
          id={id}
          value={saisie[cle]}
          onChange={(e) => setSaisie({ ...saisie, [cle]: e.target.value })}
          placeholder={placeholder}
          disabled={enCours}
          autoFocus={cle === 'numero'}
          style={STYLE_CHAMP}
        />
      </div>
    )
    return (
      <div className="flex flex-col gap-3" style={{ padding: '10px 0 12px', borderTop: '1px solid var(--color-gray-10)' }}>
        <div className="flex gap-2">
          {champ('ede-numero', 'N° EDE', 'numero', 'NVS50312-G')}
          {champ('ede-rev', 'Rév.', 'rev', '01', 64)}
        </div>
        {champ('ede-pages', 'Page(s)', 'pages', '12, 12-14 ou 12, 18')}
        <span style={{ fontSize: 12, color: 'var(--color-gray-70)' }}>
          La référence complète se colle telle quelle : « NVS50312-G-EDE Rev 01 ».
        </span>
        <BoutonsFormulaire enCours={enCours} onAnnuler={() => setSaisie(null)} onEnregistrer={() => void enregistrer()} />
      </div>
    )
  }

  const crayon = peutEditer ? (
    <BoutonCrayon
      libelle={issue.numeroEDE ? 'Modifier le schéma électrique' : 'Renseigner le schéma électrique'}
      onClick={ouvrir}
    />
  ) : undefined

  if (!ede) {
    return <LigneSuivi label="EDE" discrete action={crayon}>Non renseigné{icone}{bloc}</LigneSuivi>
  }

  return (
    <>
      <LigneSuivi label="EDE" discrete={ede.reprise} action={crayon}>
        {libelleEDE(ede.numero, ede.rev)}
        {icone}
        {ede.reprise && (
          <span style={{ display: 'block', fontSize: 12 }}>reprise de l’équipement (dernière révision connue)</span>
        )}
        {ede.revPlusRecente && (
          <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--color-gray-90)' }}>
            Plus récente connue : Rev {ede.revPlusRecente}
          </span>
        )}
        {bloc}
      </LigneSuivi>
      <LigneSuivi label="Page(s)" discrete={!issue.pageEDE}>
        {issue.pageEDE ?? 'Non renseignée'}
      </LigneSuivi>
    </>
  )
}
